import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  NavLink,
} from 'react-router-dom';

import {
  Badge,
  Card,
  Col,
  Container,
  Row,
} from 'react-bootstrap';

import {
  FaEdit,
} from 'react-icons/fa';

import API from '../../api/axios';

import SmartTable from '../../components/Smart/SmartTable';
import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';

import {
  useFormat,
} from '../../components/FormatContext';

import useCompanyContext from '../../hooks/useCompanyContext';

const DEFAULT_PAGE_SIZE =
  10;

const INITIAL_PAGE = {
  page_number: 1,
  total_pages: 1,
};

const INITIAL_META = {
  total: 0,
  page: 1,
  pageSize:
    DEFAULT_PAGE_SIZE,
  totalPages: 1,
};

const INITIAL_FILTERS = {
  status: [],
};

const SEARCH_TYPE = [
  {
    key:
      'referenceNumber',
    label:
      'Reservation Number',
  },
  {
    key:
      'customerEmail',
    label:
      'Customer email',
  },
  {
    key:
      'pickupLocation',
    label:
      'Pickup location',
  },
];

const FILTER_OPTIONS = {
  status: [
    'Under Review',
    'Approved',
    'Rejected',
  ],
};

const normalizeRows = (
  items,
) =>
  (items || []).map(
    (item) => ({
      id:
        item?.id,

      bookingNumber:
        item?.rentalOrder
          ?.referenceNumber ||
        '',

      customerName:
        item?.customer
          ?.name ??
        '',

      customerLastname:
        item?.customer
          ?.lastName ??
        '',

      customerEmail:
        item?.customer
          ?.email ??
        '',

      createDate:
        item?.createDate,

      pickupShopId:
        item?.shop?.id ??
        '',

      pickupShopName:
        item?.shop?.name ??
        '',

      pickupShopCity:
        item?.rentalOrder
          ?.pickupLocationName ??
        '',

      rmbServiceRating:
        item?.ratings
          ?.rmbServiceRating ??
        null,

      shopServiceRating:
        item?.ratings
          ?.shopServiceRating ??
        null,

      motorcycleStatusRating:
        item?.ratings
          ?.motorcycleStatusRating ??
        null,

      status:
        item?.status ??
        'UNDER_REVIEW',

      isPassedRmb:
        item?.flags
          ?.isPassedRmb ??
        false,

      isPassedShop:
        item?.flags
          ?.isPassedShop ??
        false,
    }),
  );

const getRatingClass = (
  rating,
) => {
  if (
    rating === null ||
    rating === undefined
  ) {
    return 'text-warning-700';
  }

  if (rating >= 4) {
    return 'text-success-700';
  }

  if (rating === 3) {
    return 'text-warning-700';
  }

  return 'text-danger-700';
};

const RatingItem = ({
  label,
  value,
}) => (
  <div className="d-flex align-items-center mb-1">
    <span className="me-2">
      {label}:
    </span>

    <div
      className={`d-inline-flex align-items-center justify-content-center rounded ${getRatingClass(
        value,
      )}`}
    >
      <span className="small fs-6">
        {value ??
          'N/A'}
      </span>
    </div>
  </div>
);

const STATUS_COLOR_MAP = {
  UNDER_REVIEW: {
    bg:
      'bgColor-pending',
    text:
      'text-warning-700',
  },

  APPROVED: {
    bg:
      'bgColor-accepted',
    text:
      'text-success-700',
  },

  REJECTED: {
    bg:
      'bgColor-cancelled',
    text:
      'text-danger-700',
  },
};

const STATUS_LABEL_MAP = {
  UNDER_REVIEW:
    'Pending Review',

  APPROVED:
    'Approved',

  REJECTED:
    'Rejected',
};

const Reviews = () => {
  const {
    baseFormatters,
  } = useFormat();

  const {
    isAdmin,
    currentCompanyID,
    hasSpecificCompany,
    buildCompanyUrl,
  } = useCompanyContext();

  const [
    page,
    setPage,
  ] = useState(
    INITIAL_PAGE,
  );

  const [
    searchConditions,
    setSearchConditions,
  ] = useState([]);

  const [
    filters,
    setFilters,
  ] = useState(
    INITIAL_FILTERS,
  );

  const [
    reviews,
    setReviews,
  ] = useState([]);

  const [
    meta,
    setMeta,
  ] = useState(
    INITIAL_META,
  );

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    errorMsg,
    setErrorMsg,
  ] = useState('');

  const requestRef =
    useRef(0);

  const companyScopeKey =
    hasSpecificCompany
      ? String(
          currentCompanyID,
        )
      : 'ALL';

  const previousCompanyScopeRef =
    useRef(
      companyScopeKey,
    );

  const fetchReviews =
    useCallback(
      async () => {
        const companyChanged =
          previousCompanyScopeRef
            .current !==
          companyScopeKey;

        previousCompanyScopeRef.current =
          companyScopeKey;

        const requestedPage =
          companyChanged
            ? 1
            : page.page_number ||
              1;

        const requestId =
          ++requestRef.current;

        try {
          setLoading(
            true,
          );

          setErrorMsg('');

          const params =
            new URLSearchParams();

          params.set(
            'page',
            String(
              requestedPage,
            ),
          );

          params.set(
            'pageSize',
            String(
              meta.pageSize ||
                DEFAULT_PAGE_SIZE,
            ),
          );

          params.set(
            'sortBy',
            'createDate',
          );

          params.set(
            'order',
            'desc',
          );

          if (
            hasSpecificCompany
          ) {
            params.set(
              'supplierId',
              String(
                currentCompanyID,
              ),
            );
          }

          searchConditions.forEach(
            (condition) => {
              const type =
                condition?.type ??
                condition?.key ??
                condition?.field;

              const value =
                (
                  condition?.value ??
                  condition?.keyword ??
                  condition?.search ??
                  condition?.text ??
                  ''
                )
                  .toString()
                  .trim();

              if (
                !type ||
                !value
              ) {
                return;
              }

              switch (type) {
                case 'referenceNumber':
                  params.set(
                    'referenceNumber',
                    value,
                  );
                  break;

                case 'customerEmail':
                  params.set(
                    'customerEmail',
                    value,
                  );
                  break;

                case 'pickupLocation':
                  params.set(
                    'pickupLocation',
                    value,
                  );
                  break;

                default:
                  break;
              }
            },
          );

          filters.status.forEach(
            (status) => {
              if (status) {
                params.append(
                  'status',
                  String(
                    status,
                  ),
                );
              }
            },
          );

          const {
            data,
          } =
            await API.get(
              `/rental-order-review?${params.toString()}`,
            );

          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          const items =
            data?.items ??
            data?.data ??
            [];

          const nextMeta =
            data?.meta ??
            INITIAL_META;

          setReviews(
            normalizeRows(
              items,
            ),
          );

          setMeta(
            nextMeta,
          );

          const nextPage =
            nextMeta.page ||
            1;

          const nextTotalPages =
            nextMeta.totalPages ||
            1;

          setPage(
            (prev) => {
              if (
                prev.page_number ===
                  nextPage &&
                prev.total_pages ===
                  nextTotalPages
              ) {
                return prev;
              }

              return {
                ...prev,

                page_number:
                  nextPage,

                total_pages:
                  nextTotalPages,
              };
            },
          );
        } catch (error) {
          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          setErrorMsg(
            error?.response
              ?.data
              ?.message ||
              error?.message ||
              'Failed to load reviews',
          );

          setReviews([]);

          setMeta(
            INITIAL_META,
          );

          setPage(
            (prev) => ({
              ...prev,
              total_pages:
                1,
            }),
          );
        } finally {
          if (
            requestId ===
            requestRef.current
          ) {
            setLoading(
              false,
            );
          }
        }
      },
      [
        companyScopeKey,
        currentCompanyID,
        hasSpecificCompany,
        page.page_number,
        meta.pageSize,
        searchConditions,
        filters.status,
      ],
    );

  const handleSetSearchConditions =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setSearchConditions(
          (prev) =>
            typeof valueOrUpdater ===
            'function'
              ? valueOrUpdater(
                  prev,
                )
              : valueOrUpdater,
        );

        setPage(
          (prev) =>
            prev.page_number ===
            1
              ? prev
              : {
                  ...prev,
                  page_number:
                    1,
                },
        );
      },
      [],
    );

  const handleSetFilters =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setFilters(
          (prev) =>
            typeof valueOrUpdater ===
            'function'
              ? valueOrUpdater(
                  prev,
                )
              : valueOrUpdater,
        );

        setPage(
          (prev) =>
            prev.page_number ===
            1
              ? prev
              : {
                  ...prev,
                  page_number:
                    1,
                },
        );
      },
      [],
    );

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const columns =
    useMemo(() => {
      const baseColumns = [
        {
          key:
            'bookingNumber',

          label:
            'Booking Number',
        },

        {
          keys: [
            'customerName',
            'customerLastname',
            'customerEmail',
            'createDate',
          ],

          label:
            'Customer Info',

          render:
            (item) => (
              <div className="d-flex flex-column">
                <span>
                  {
                    item.customerName
                  }{' '}
                  {
                    item.customerLastname
                  }
                </span>

                <span>
                  {
                    item.customerEmail
                  }
                </span>

                <span className="fw-medium">
                  {item.createDate
                    ? baseFormatters.datetime.format(
                        new Date(
                          item.createDate,
                        ),
                      )
                    : '-'}
                </span>
              </div>
            ),
        },

        {
          keys: [
            'pickupShopName',
            'pickupShopCity',
          ],

          label:
            'Pickup Shop',

          render:
            (item) => (
              <div className="d-flex flex-column">
                <span className="fw-medium">
                  {item.pickupShopName ||
                    '-'}
                </span>

                <span className="text-muted">
                  {item.pickupShopCity ||
                    'Location not available'}
                </span>
              </div>
            ),
        },

        {
          keys: [
            'rmbServiceRating',
            'shopServiceRating',
            'motorcycleStatusRating',
          ],

          label:
            'Ratings',

          render:
            (item) => (
              <div className="d-flex flex-column">
                <RatingItem
                  label="RMB"
                  value={
                    item.rmbServiceRating
                  }
                />

                <RatingItem
                  label="Shop"
                  value={
                    item.shopServiceRating
                  }
                />

                <RatingItem
                  label="Motorcycle"
                  value={
                    item.motorcycleStatusRating
                  }
                />
              </div>
            ),
        },
      ];

      const actionColumn = {
        key:
          'actions',

        label:
          'Actions',

        render:
          (item) => (
            <div className="d-flex gap-2 align-items-center">
              <NavLink
                to={buildCompanyUrl(
                  `/reviews/edit/${item.id}`,
                )}
                className="btn btn-edit p-0"
                title="Edit"
              >
                <FaEdit
                  size={20}
                  style={{
                    marginLeft:
                      '4px',
                  }}
                />
              </NavLink>
            </div>
          ),
      };

      if (!isAdmin) {
        return [
          ...baseColumns,
          actionColumn,
        ];
      }

      const adminStatusColumn = {
        keys: [
          'status',
          'isPassedRmb',
          'isPassedShop',
        ],

        label:
          'Review Status',

        render:
          (item) => {
            const status =
              item.status ||
              'UNDER_REVIEW';

            const statusColor =
              STATUS_COLOR_MAP[
                status
              ] ||
              STATUS_COLOR_MAP
                .UNDER_REVIEW;

            const statusLabel =
              STATUS_LABEL_MAP[
                status
              ] ||
              'Pending Review';

            const rmbColor =
              item.isPassedRmb
                ? STATUS_COLOR_MAP
                    .APPROVED
                : STATUS_COLOR_MAP
                    .REJECTED;

            const shopColor =
              item.isPassedShop
                ? STATUS_COLOR_MAP
                    .APPROVED
                : STATUS_COLOR_MAP
                    .REJECTED;

            return (
              <div className="d-flex flex-column gap-2">
                <div
                  className={`d-inline-flex align-items-center justify-content-center rounded px-2 py-1 border border-1 ${statusColor.bg} ${statusColor.text}`}
                  style={{
                    width:
                      'fit-content',
                    minWidth:
                      '120px',
                  }}
                >
                  <span className="small fw-medium">
                    {
                      statusLabel
                    }
                  </span>
                </div>

                {(status ===
                  'APPROVED' ||
                  status ===
                    'REJECTED') && (
                  <div className="d-flex flex-wrap gap-2">
                    <div
                      className={`d-inline-flex align-items-center justify-content-center rounded px-2 py-1 border border-1 ${rmbColor.bg} ${rmbColor.text}`}
                    >
                      <span className="small fw-medium">
                        RMB:{' '}
                        {item.isPassedRmb
                          ? 'Published'
                          : 'Hidden'}
                      </span>
                    </div>

                    <div
                      className={`d-inline-flex align-items-center justify-content-center rounded px-2 py-1 border border-1 ${shopColor.bg} ${shopColor.text}`}
                    >
                      <span className="small fw-medium">
                        Shop:{' '}
                        {item.isPassedShop
                          ? 'Published'
                          : 'Hidden'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          },
      };

      return [
        ...baseColumns,
        adminStatusColumn,
        actionColumn,
      ];
    }, [
      baseFormatters.datetime,
      buildCompanyUrl,
      isAdmin,
    ]);

  return (
    <Container
      fluid
      className="d-flex flex-column"
    >
      <Row className="mb-4">
        <Col sm={8}>
          <span className="fw-semibold">
            Reviews List
          </span>
        </Col>

        <Col
          sm={4}
          className="text-end"
        >
          <span className="fw-semibold">
            <NavLink
              to={buildCompanyUrl(
                '/dashboard',
              )}
              className="block-link"
            >
              Dashboard
            </NavLink>

            {' - Reviews List'}
          </span>
        </Col>
      </Row>

      <Row>
        <Col sm={12}>
          <Card className="h-100 p-2">
            <Card.Body className="px-2 pt-2 pb-5">
              <Row>
                <Col md={12}>
                  <SmartTopTool
                    searchType={
                      SEARCH_TYPE
                    }
                    searchConditions={
                      searchConditions
                    }
                    setSearchConditions={
                      handleSetSearchConditions
                    }
                    filters={
                      filters
                    }
                    filterOptions={
                      FILTER_OPTIONS
                    }
                    setFilters={
                      handleSetFilters
                    }
                  />
                </Col>

                {errorMsg && (
                  <Col
                    md={12}
                    className="mt-2"
                  >
                    <div className="alert alert-danger mb-2">
                      {
                        errorMsg
                      }
                    </div>
                  </Col>
                )}

                <Col md={12}>
                  <SmartTable
                    data={
                      reviews
                    }
                    columns={
                      columns
                    }
                    loading={
                      loading
                    }
                    emptyState={
                      <div className="text-center py-5 text-muted">
                        <p>
                          {errorMsg ||
                            (loading
                              ? 'Loading...'
                              : 'No matching reviews found')}
                        </p>
                      </div>
                    }
                  />
                </Col>

                <Col
                  md={12}
                  className="d-flex justify-content-end"
                >
                  <SmartPagination
                    page={
                      page
                    }
                    setPage={
                      setPage
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Reviews;